import type { InterfaceTheme } from '../tokens.js';

/**
 * Classic Green — the default interface theme.
 *
 * A two-pane green messenger: conversation list beside the thread on a wide
 * screen, one at a time on a phone, tinted bubbles with tails, a faint motif
 * behind the thread. That arrangement is the common vocabulary of the whole
 * category and is what makes the surface legible on sight.
 *
 * Every value here is this product's own. An earlier revision of this file
 * carried another messenger's palette and metrics verbatim — its exact greens,
 * its 59px header, its 420px column, its 7.5px bubble — while its comment
 * claimed the opposite. Reproducing a competitor's look and feel that precisely
 * is a different thing from working in a familiar idiom, and the comment saying
 * "our own values" made it worse rather than better: an inaccurate note in a
 * place like this reads as knowing.
 *
 * So the palette is a cooler, deeper emerald that is nobody's brand colour, the
 * neutrals are green-tinted rather than blue, and the metrics are the theme's
 * own. Nothing is theme-specific by contract: every value fills a slot in
 * InterfaceTokens, so another theme replaces it wholesale.
 */

/**
 * The chat wallpaper.
 *
 * Original artwork — a sparse, low-contrast motif of small line marks at
 * irregular positions, drawn for this product. Encoded as a data URI so it
 * needs no network request: the Content Security Policy forbids remote origins
 * on conversation pages (concept §5).
 */
function doodle(stroke: string, opacity: number): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='250' height='250' viewBox='0 0 260 260'>
<g fill='none' stroke='${stroke}' stroke-opacity='${opacity}' stroke-width='1.15' stroke-linecap='round' stroke-linejoin='round'>
<path d='M22 34h20a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4h-9l-6 5v-5h-5a4 4 0 0 1-4-4V38a4 4 0 0 1 4-4z'/>
<circle cx='96' cy='30' r='9'/><path d='M96 25v5l3 2'/>
<path d='M150 26l4 9 10 1-7 7 2 10-9-5-9 5 2-10-7-7 10-1z'/>
<path d='M206 24c6 0 10 4 10 9s-4 9-10 9-10-4-10-9 4-9 10-9zM206 42v8'/>
<path d='M30 96c5-6 13-6 18 0M24 90c8-9 22-9 30 0'/>
<rect x='92' y='84' width='22' height='28' rx='3'/><path d='M97 92h12M97 98h12M97 104h7'/>
<path d='M148 86l16 8-16 8z'/>
<circle cx='210' cy='96' r='11'/><path d='M205 96h10M210 91v10'/>
<path d='M26 152h18l4 6h-26z'/><circle cx='35' cy='168' r='7'/>
<path d='M88 148c8-4 18 2 18 10s-10 14-18 10'/>
<path d='M146 150h20v18h-8l-5 5v-5h-7z'/>
<path d='M204 148v18M198 154l6-6 6 6'/>
<path d='M28 214c6-8 16-8 22 0'/><circle cx='39' cy='206' r='5'/>
<path d='M92 206h20a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H98l-6 4v-4a3 3 0 0 1-3-3v-10a3 3 0 0 1 3-3z'/>
<path d='M150 204l6 12h-12z'/><path d='M150 222v-3'/>
<circle cx='208' cy='212' r='10'/><path d='M203 212l4 4 8-8'/>
</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * System-first, so the interface is set in the reader's own UI face. Nothing is
 * fetched — the CSP allows no remote origin, a font CDN included.
 */
const SANS =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif";

export const classicGreenTheme: InterfaceTheme = {
  id: 'classic_green',
  label: 'Classic Green',
  description: 'A two-pane green messenger layout, in Kairelo’s own palette and proportions.',

  light: {
    appBackground: '#d5dbdb',
    sidebarBackground: '#ffffff',
    headerBackground: '#eef1f1',
    chatBackground: '#e9ecea',
    chatBackgroundPattern: doodle('#6d7d74', 0.035),
    composerBackground: '#eef1f1',
    inputBackground: '#ffffff',
    overlayBackground: '#ffffff',

    incomingBubble: '#ffffff',
    incomingBubbleText: '#12201d',
    outgoingBubble: '#d3f0e4',
    outgoingBubbleText: '#12201d',
    quoteBackground: 'rgba(0, 0, 0, 0.05)',
    quoteAccent: '#12b183',

    textPrimary: '#12201d',
    textSecondary: '#5f716c',
    textMeta: 'rgba(18, 32, 29, 0.55)',
    textOnAccent: '#ffffff',

    border: '#e6ebe9',
    divider: '#e6ebe9',
    icon: '#58696a',
    iconActive: '#12201d',

    /*
     * Deep enough that white text on it measures 4.77:1. The value it replaced
     * — and the competitor's own — sat at 3.03:1, and its badge green at
     * 1.98:1, both of which fail AA outright. Measured in scripts, not eyeballed.
     */
    accent: '#0a8268',
    accentHover: '#086e58',
    hover: '#f4f6f5',
    selected: '#eaefed',
    unreadBadge: '#0a8268',
    unreadBadgeText: '#ffffff',
    tickRead: '#4f9ed6',
    systemBubble: '#fdf0d8',
    systemBubbleText: '#58696a',
    danger: '#d92d43',

    fontFamily: SANS,
    bubbleRadius: '12px',
    bubbleTailRadius: '4px',
    avatarRadius: '50%',
    headerHeight: '56px',
    composerMinHeight: '58px',
    sidebarWidth: '384px',
    rowHeight: '66px',
    bubbleMaxWidth: '70%',
    bubbleShadow: '0 1px 1px rgba(12, 26, 22, 0.10)',
    panelShadow: '0 3px 10px rgba(12, 26, 22, 0.22)',
  },

  dark: {
    appBackground: '#0d1614',
    sidebarBackground: '#121e1b',
    headerBackground: '#1e2b28',
    chatBackground: '#0c1715',
    chatBackgroundPattern: doodle('#c2d0cb', 0.028),
    composerBackground: '#1e2b28',
    inputBackground: '#273733',
    overlayBackground: '#212f2c',

    incomingBubble: '#1e2b28',
    incomingBubbleText: '#e7ecea',
    outgoingBubble: '#0a4f43',
    outgoingBubbleText: '#e7ecea',
    quoteBackground: 'rgba(255, 255, 255, 0.06)',
    quoteAccent: '#12b183',

    textPrimary: '#e7ecea',
    textSecondary: '#8a9a95',
    textMeta: 'rgba(231, 236, 234, 0.6)',
    textOnAccent: '#0d1614',

    border: '#212e2a',
    divider: '#212e2a',
    icon: '#abbab5',
    iconActive: '#e7ecea',

    accent: '#12b183',
    accentHover: '#1ac798',
    hover: '#1e2b28',
    selected: '#273733',
    unreadBadge: '#12b183',
    unreadBadgeText: '#0d1614',
    tickRead: '#4f9ed6',
    systemBubble: '#172422',
    systemBubbleText: '#8a9a95',
    danger: '#ef6274',

    fontFamily: SANS,
    bubbleRadius: '12px',
    bubbleTailRadius: '4px',
    avatarRadius: '50%',
    headerHeight: '56px',
    composerMinHeight: '58px',
    sidebarWidth: '384px',
    rowHeight: '66px',
    bubbleMaxWidth: '70%',
    bubbleShadow: '0 1px 1px rgba(0, 0, 0, 0.24)',
    panelShadow: '0 3px 10px rgba(0, 0, 0, 0.42)',
  },
};
